/**
 * Verbes du tableau de bord.
 *
 * Il ne requête presque rien lui-même : il réutilise les dépôts des autres
 * domaines et leurs fonctions de calcul. C'est ce qui garantit que le montant
 * « impayés » du tableau de bord et celui de la fiche client sont le même
 * nombre — huit requêtes séparées finissaient toujours par diverger.
 */
import { ActionLevel } from "../../levels.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import type { InvoiceRepository } from "../invoices/services.js";
import { computeReceivables } from "../invoices/types.js";
import type { ProjectRepository } from "../projects/repository.js";
import { OPEN_PROJECT_STATUSES, driftSeverity, isDrifting } from "../projects/types.js";
import type { QuoteRepository } from "../quotes/repository.js";
import { computeChange } from "./types.js";
import type { DashboardKpis, DaySummary } from "./types.js";

/** Agrégats que seule une requête peut fournir. */
export interface DashboardRepository {
  /** Encaissements sur la période, bornes incluses. */
  revenueBetween(ctx: ExecutionContext, from: string, to: string): Promise<number>;
  /** Marge moyenne des devis créés sur la période, en points. */
  averageQuoteMargin(ctx: ExecutionContext, from: string, to: string): Promise<number>;
  /** Vue `v_einvoice_compliance`. Null si la vue n'existe pas encore. */
  einvoiceCompliancePercent(ctx: ExecutionContext): Promise<number | null>;
}

export interface DashboardDeps {
  dashboard: DashboardRepository;
  invoices: InvoiceRepository;
  quotes: QuoteRepository;
  projects: ProjectRepository;
}

/**
 * Formate une date locale en AAAA-MM-JJ.
 *
 * Surtout pas `toISOString()` : les bornes du mois sont construites en heure
 * locale, et `toISOString` les reconvertit en UTC. À Paris en septembre, le
 * 1er du mois à 00 h devient le 31 août à 22 h — donc un encaissement du
 * premier jour du mois tombait dans le chiffre d'affaires du mois précédent.
 */
const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

/** Bornes du mois courant et du précédent, en dates locales. */
function periods(today: Date) {
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const startOfPrev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const endOfPrev = new Date(today.getFullYear(), today.getMonth(), 0);
  return {
    current: { from: iso(startOfMonth), to: iso(today) },
    previous: { from: iso(startOfPrev), to: iso(endOfPrev) },
  };
}

export function createDashboardServices(deps: DashboardDeps, now: () => Date = () => new Date()) {
  async function kpis(ctx: ExecutionContext): Promise<DashboardKpis> {
    const { current, previous } = periods(now());

    const [
      revenueCurrent,
      revenuePrevious,
      marginCurrent,
      marginPrevious,
      compliance,
      allInvoices,
      pendingQuotes,
      activeProjects,
    ] = await Promise.all([
      deps.dashboard.revenueBetween(ctx, current.from, current.to),
      deps.dashboard.revenueBetween(ctx, previous.from, previous.to),
      deps.dashboard.averageQuoteMargin(ctx, current.from, current.to),
      deps.dashboard.averageQuoteMargin(ctx, previous.from, previous.to),
      deps.dashboard.einvoiceCompliancePercent(ctx),
      deps.invoices.list(ctx),
      deps.quotes.list(ctx, { status: ["draft", "sent"] }),
      deps.projects.list(ctx, { status: [...OPEN_PROJECT_STATUSES] }),
    ]);

    // Les impayés se déduisent des dates d'échéance, pas du statut `overdue` :
    // ce statut est posé par un cron nocturne, donc une facture échue ce matin
    // n'y figurait pas encore. Le tableau de bord affichait un montant en
    // retard d'une journée sur la réalité.
    const receivables = computeReceivables(allInvoices);

    return {
      revenue: {
        current: revenueCurrent,
        previous: revenuePrevious,
        change: computeChange(revenueCurrent, revenuePrevious),
      },
      quoteMargin: {
        current: marginCurrent,
        previous: marginPrevious,
        // Une marge se compare en points, pas en pourcentage de pourcentage.
        change: Math.round((marginCurrent - marginPrevious) * 10) / 10,
      },
      pendingQuotes: pendingQuotes.length,
      activeProjects: activeProjects.length,
      totalDue: receivables.totalDue,
      overdueDue: receivables.overdueDue,
      overdueCount: receivables.overdueCount,
      unsentDue: receivables.unsentDue,
      unsentCount: receivables.unsentCount,
      unsentOverdueDue: receivables.unsentOverdueDue,
      einvoiceCompliancePercent: compliance,
    };
  }

  const kpisTableauDeBord = defineService<{ confirmationId?: string }, DashboardKpis>({
    name: "kpis_tableau_de_bord",
    level: ActionLevel.Read,
    handler: (ctx) => kpis(ctx),
  });

  /**
   * Réponse à « où on en est ? ».
   *
   * Volontairement borné : les cinq devis qui attendent, les chantiers qui
   * dérivent, les cinq impayés les plus anciens. Une liste exhaustive obligerait
   * le patron à faire le tri lui-même, ce qui est exactement ce qu'on lui
   * évite.
   */
  const resumeJournee = defineService<{ confirmationId?: string }, DaySummary>({
    name: "resume_journee",
    level: ActionLevel.Read,
    handler: async (ctx) => {
      const [k, quotes, projects, invoices] = await Promise.all([
        kpis(ctx),
        deps.quotes.list(ctx, { status: ["sent", "viewed"] }),
        deps.projects.list(ctx, {
          status: ["planned", "permit_pending", "in_progress", "paused"],
        }),
        deps.invoices.list(ctx),
      ]);

      return {
        kpis: k,
        pendingQuotes: quotes.slice(0, 5),
        // La même règle que `chantiers_en_derive`, et non une copie : les
        // deux avaient fini par ne plus dire la même chose.
        driftingProjects: projects
          .filter((p) => isDrifting(p.health))
          .sort((a, b) => driftSeverity(a.health) - driftSeverity(b.health)),
        overdueInvoices: invoices
          .filter((i) => i.balance.isOverdue)
          .sort((a, b) => b.balance.daysLate - a.balance.daysLate)
          .slice(0, 5),
      };
    },
  });

  return { kpisTableauDeBord, resumeJournee };
}

export type DashboardServices = ReturnType<typeof createDashboardServices>;
